import { logger } from "@octopusdeploy/logging";
import type { Client, RequestContext } from "../client";
import type { RouteArgs } from "../resolver";
import type { CustomFieldResource, CustomFieldScopes, GetCustomFieldsAcrossAllSpacesBffResponse } from "../resources/customFieldResource";
import type { ResourceCollection } from "../resources/resourceCollection";
import type { GetAllTenantTagSetsAcrossAllSpacesBffResponse } from "../resources/tagSetResource";
import type { ListArgs } from "./basicRepository";
export type CustomFieldListArgs = ListArgs & RouteArgs & {
    partialName?: string;
    scopes?: string[];
    take?: number;
};
export class CustomFieldRepository {
    private client: Client;
    private requestContext: RequestContext;
    private urlTemplate: string;
    private maxTake = 10000;
    constructor(client: Client, requestContext: RequestContext) {
        this.client = client;
        this.requestContext = requestContext;
        this.urlTemplate = "~/api/{spaceId}/customfields/{id}{?skip,take,ids,partialName,scopes}";
    }
    private appendSpaceId(args: RouteArgs) {
        return { ...args, spaceId: this.client.spaceId };
    }
    private syncCustomFieldValuesToTags(customField: CustomFieldResource): CustomFieldResource {
        customField.Tags = customField.Tags ?? [];
        if (customField.Values && customField.Values.length > 0) {
            customField.Values.forEach((customFieldValue) => {
                customFieldValue.CanonicalTagName = customFieldValue.CanonicalCustomFieldValueName ?? "";
            });
            customField.Tags = [...customField.Values, ...customField.Tags];
        }
        return customField;
    }
    private syncTagsToCustomFieldValues(customFieldResource: CustomFieldResource): CustomFieldResource {
        if (customFieldResource.Tags && customFieldResource.Tags.length > 0) {
            customFieldResource.Values = [...customFieldResource.Tags];
            customFieldResource.Values.forEach((customField) => {
                customField.CanonicalCustomFieldValueName = customField.CanonicalTagName ?? "";
            });
        }
        return customFieldResource;
    }
    del(resource: CustomFieldResource): Promise<unknown> {
        return this.client.del(this.requestContext, this.urlTemplate, resource, this.appendSpaceId({ id: resource.Id }));
    }
    list(args?: CustomFieldListArgs): Promise<ResourceCollection<CustomFieldResource>> {
        return this.client.get<ResourceCollection<CustomFieldResource>>(this.requestContext, this.urlTemplate, this.appendSpaceId(args ?? {})).then((result) => {
            if (result.Items) {
                result.Items.forEach((customField) => this.syncCustomFieldValuesToTags(customField));
            }
            return result;
        });
    }
    getAllByScopes(scopes: string[]): Promise<CustomFieldResource[]> {
        return this.client.get<ResourceCollection<CustomFieldResource>>(this.requestContext, this.urlTemplate, this.appendSpaceId({ scopes, skip: 0, take: this.maxTake })).then((result) => {
            if (result.Items) {
                result.Items.forEach((customField) => this.syncCustomFieldValuesToTags(customField));
            }
            return result.Items;
        });
    }
    get(id: string, args?: CustomFieldListArgs): Promise<CustomFieldResource> {
        const allArgs = this.appendSpaceId({ ...args, id });
        return this.client.get<CustomFieldResource>(this.requestContext, this.urlTemplate, allArgs).then((customField) => this.syncCustomFieldValuesToTags(customField));
    }
    all(args?: CustomFieldListArgs): Promise<CustomFieldResource[]> {
        if (args !== undefined && args.ids instanceof Array && args.ids.length === 0) {
            return new Promise<CustomFieldResource[]>((res) => {
                res([]);
            });
        }
        const allArgs = this.appendSpaceId({ ...args, id: "all" });
        return this.client.get<CustomFieldResource[]>(this.requestContext, this.urlTemplate, allArgs).then((customFields) => {
            customFields.forEach((customFields) => this.syncCustomFieldValuesToTags(customFields));
            return customFields;
        });
    }
    allCustomFieldsAcrossAllSpaces(args?: {
        filterBySpace?: string;
        filterByExcludedSpace?: string;
        scopes?: CustomFieldScopes[];
    }): Promise<GetCustomFieldsAcrossAllSpacesBffResponse> {
        return this.client.get<GetCustomFieldsAcrossAllSpacesBffResponse>(this.requestContext, "~/bff/customfields{?filterBySpace,filterByExcludedSpace,scopes}", args);
    }
    save(resource: CustomFieldResource): Promise<CustomFieldResource> {
        logger.info("save");
        if (!resource.Id) {
            return this.create(resource, {});
        }
        else {
            return this.modify(resource, { id: resource.Id });
        }
    }
    create(resource: CustomFieldResource, args: RouteArgs): Promise<CustomFieldResource> {
        resource = this.syncTagsToCustomFieldValues(resource);
        return this.client.create<CustomFieldResource, CustomFieldResource>(this.requestContext, this.urlTemplate, resource, this.appendSpaceId(args)).then((customField) => this.syncCustomFieldValuesToTags(customField));
    }
    modify(resource: CustomFieldResource, args: RouteArgs): Promise<CustomFieldResource> {
        logger.info("modify: {resource}", { resource });
        resource = this.syncTagsToCustomFieldValues(resource);
        logger.info("modify: {resource}", { resource });
        return this.client.update(this.requestContext, this.urlTemplate, resource, this.appendSpaceId(args)).then((customField) => this.syncCustomFieldValuesToTags(customField));
    }
    sort(ids: string[]) {
        return this.client.put(this.requestContext, "~/api/{spaceId}/customfields/sortorder", ids, this.appendSpaceId({}));
    }
    async allTagSetsAcrossAllSpaces(args?: {
        filterBySpace?: string;
        filterByExcludedSpace?: string;
        scope?: CustomFieldScopes;
    }): Promise<GetAllTenantTagSetsAcrossAllSpacesBffResponse> {
        args = { ...args, scope: "Tenant" };
        const response = await this.client.get<GetCustomFieldsAcrossAllSpacesBffResponse>(this.requestContext, "~/bff/customfields{?filterBySpace,filterByExcludedSpace,scope}", args);
        response.CustomFields.forEach((customField) => this.syncCustomFieldValuesToTags(customField));
        return {
            TagSets: response.CustomFields,
        };
    }
}
